import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { StripeProvider } from '../../contexts/StripeContext';
import StripePaymentForm from '../../components/payment/StripePaymentForm';
import { paymentService } from '../../services/paymentService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const CheckoutPage = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState('');
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paymentState, setPaymentState] = useState('select'); // select, payment, success
  const [error, setError] = useState('');

  useEffect(() => {
    loadSubscriptionPlans();
  }, []);

  const loadSubscriptionPlans = async () => {
    try {
      const plans = await paymentService?.getSubscriptionPlans();
      setSubscriptionPlans(plans);
      if (plans?.length > 0) {
        setSelectedPlan(plans?.[0]);
      }
    } catch (error) {
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (!selectedPlan || !user || !userProfile) {
      setError('Please select a plan and ensure you are logged in');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare order data
      const orderData = {
        items: [{
          name: selectedPlan?.name,
          price: selectedPlan?.amount,
          quantity: 1,
          plan_id: selectedPlan?.id
        }],
        subtotal: selectedPlan?.amount,
        total: selectedPlan?.amount,
        currency: selectedPlan?.currency || 'USD',
        billing_cycle: billingCycle
      };

      // Prepare customer info
      const customerInfo = {
        userId: user?.id,
        firstName: userProfile?.first_name || 'User',
        lastName: userProfile?.last_name || 'Name',
        email: userProfile?.email || user?.email,
        phone: userProfile?.phone,
        stripeCustomerId: userProfile?.stripe_customer_id,
        billing: {
          address_line_1: '123 Default Street', // In a real app, collect this from user
          city: 'Default City',
          state: 'CA',
          postal_code: '12345',
          country: 'US'
        }
      };

      // Create payment intent
      const paymentData = await paymentService?.createPaymentIntent(orderData, customerInfo);
      setClientSecret(paymentData?.clientSecret);
      setPaymentState('payment');
    } catch (error) {
      setError(error?.message || 'Failed to initialize payment');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (successData) => {
    setPaymentState('success');
    // Redirect after showing success message
    setTimeout(() => {
      window.location.href = '/subscription-management';
    }, 2000);
  };

  const handlePaymentError = (error) => {
    setError(error?.message || 'Payment failed');
    setPaymentState('select');
  };

  const handleLogin = () => {
    window.location.href = '/user-login';
  };

  const handleLogout = () => {
    window.location.href = '/user-login';
  };

  if (loading && paymentState === 'select') {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onLogin={handleLogin} onLogout={handleLogout} onUpgrade={() => {}} />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading subscription plans...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onLogin={handleLogin} onLogout={handleLogout} onUpgrade={() => {}} />
        <div className="pt-16">
          <div className="max-w-md mx-auto px-4 py-16 text-center">
            <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Login Required</h2>
            <p className="text-muted-foreground mb-6">
              Please sign in to your account to proceed with checkout.
            </p>
            <Button onClick={handleLogin} className="w-full">
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <StripeProvider>
      <div className="min-h-screen bg-background">
        <Header user={user} onLogin={handleLogin} onLogout={handleLogout} onUpgrade={() => {}} />
        <div className="pt-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="CreditCard" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Checkout</h1>
                  <p className="text-muted-foreground">Complete your subscription purchase</p>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex items-center justify-center space-x-4">
                {[
                  { id: 'select', label: 'Select Plan', completed: paymentState !== 'select' },
                  { id: 'payment', label: 'Payment', completed: paymentState === 'success' },
                  { id: 'success', label: 'Complete', completed: paymentState === 'success' }
                ]?.map((step, index, steps) => (
                  <div key={step?.id} className="flex items-center">
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${step?.completed ? 'bg-green-500 text-white' : 
                        paymentState === step?.id ? 'bg-primary text-primary-foreground': 'bg-muted text-muted-foreground'}
                    `}>
                      {step?.completed ? '✓' : index + 1}
                    </div>
                    <span className="ml-2 text-sm">{step?.label}</span>
                    {index < steps?.length - 1 && (
                      <div className="w-8 h-px bg-muted mx-4" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Plan Selection */}
            {paymentState === 'select' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Plan Options */}
                <div className="lg:col-span-2">
                  <h2 className="text-xl font-semibold mb-6">Choose Your Plan</h2>
                  <div className="space-y-4">
                    {subscriptionPlans?.map((plan) => (
                      <div
                        key={plan?.id}
                        onClick={() => setSelectedPlan(plan)}
                        className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedPlan?.id === plan?.id
                            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold">{plan?.name}</h3>
                              <div className="text-2xl font-bold text-primary">
                                {paymentService?.formatAmount(plan?.amount, plan?.currency)}
                                <span className="text-sm font-normal text-muted-foreground">
                                  /{plan?.billing_interval}
                                </span>
                              </div>
                            </div>
                            {plan?.features && (
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(plan?.features)?.map(([key, value]) => (
                                  <span key={key} className="text-xs bg-muted px-2 py-1 rounded">
                                    {key}: {value}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              selectedPlan?.id === plan?.id
                                ? 'border-primary bg-primary' :'border-muted-foreground'
                            }`}>
                              {selectedPlan?.id === plan?.id && (
                                <Icon name="Check" size={12} className="text-white" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8">
                    <Button 
                      onClick={handleProceedToPayment}
                      disabled={!selectedPlan || loading}
                      size="lg"
                      className="w-full"
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      {loading ? 'Setting up payment...' : 'Proceed to Payment'}
                    </Button>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                  <div className="bg-card border border-border rounded-lg p-6 sticky top-8">
                    <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
                    {selectedPlan && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{selectedPlan?.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {billingCycle} billing
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">
                              {paymentService?.formatAmount(selectedPlan?.amount, selectedPlan?.currency)}
                            </p>
                          </div>
                        </div>
                        <div className="border-t border-border pt-4">
                          <div className="flex items-center justify-between font-semibold text-lg">
                            <span>Total</span>
                            <span>{paymentService?.formatAmount(selectedPlan?.amount, selectedPlan?.currency)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Form */}
            {paymentState === 'payment' && clientSecret && (
              <div className="max-w-2xl mx-auto">
                <h2 className="text-xl font-semibold mb-6">Payment Information</h2>
                <StripePaymentForm
                  clientSecret={clientSecret}
                  amount={selectedPlan?.amount * 100} // Convert to cents
                  currency={selectedPlan?.currency?.toLowerCase()}
                  orderData={{
                    orderId: 'pending',
                    orderNumber: 'pending'
                  }}
                  customerInfo={{
                    firstName: userProfile?.first_name || 'User',
                    lastName: userProfile?.last_name || 'Name',
                    email: userProfile?.email || user?.email,
                    phone: userProfile?.phone
                  }}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  confirmButtonText={`Pay ${paymentService?.formatAmount(selectedPlan?.amount, selectedPlan?.currency)}`}
                />
              </div>
            )}

            {/* Success State */}
            {paymentState === 'success' && (
              <div className="max-w-md mx-auto text-center py-16">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Check" size={40} className="text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h2>
                <p className="text-muted-foreground mb-6">
                  Your subscription has been activated. Redirecting to your account...
                </p>
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </StripeProvider>
  );
};

export default CheckoutPage;